import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import { TranslateService } from '@ngx-translate/core';

import { LoggerService } from '@services/logger.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';

import { GeneralDynamicFormComponent } from '../../general-dynamic-form/general-dynamic-form.component';

import { FormProperty } from 'src/app/core/models/form.models';
import { OutputSettings } from 'src/app/core/models/output-settings.models';

import { AppState } from 'src/app/core/state/app.state';

import { map } from 'rxjs';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-output-settings-general',
  templateUrl: './output-settings-general.component.html',
  styleUrls: ['./output-settings-general.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class OutputSettingsGeneralComponent
  extends GeneralDynamicFormComponent
  implements OnInit, OnChanges
{
  @Input() outputSettings: OutputSettings;
  @Input() formGroup: FormGroup; // Accept FormGroup from parent

  constructor(
    protected formService: FormsService,
    protected nomenclatureService: NomenclatureService,
    private translateService: TranslateService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  public formProperties: FormProperty[] = [
    {
      propertyName: 'title',
      propertyLabel: this.translateService.instant(
        'output-settings-general.label.output-settings-name',
      ),
      controlType: 'text',
    },
    {
      // Stored in the settings blob and returned by every read endpoint; the
      // dialog never exposed it (WAP-1638), the Admin grid edits it too.
      propertyName: 'description',
      propertyLabel: this.translateService.instant(
        'output-settings-general.label.description',
      ),
      controlType: 'textarea',
    },
    {
      propertyName: 'timezone_id',
      propertyLabel: this.translateService.instant(
        'output-settings-general.label.output-timezone',
      ),
      controlType: 'select',
      // Optional: the user may return to "no timezone"
      additionalConfig: {
        placeholder: this.translateService.instant(
          'output-settings-general.select.select-timezone',
        ),
        clearable: true,
      },
      valueOptionsAsync: this.nomenclatureService.fetchTimezones().pipe(
        map((timezones) => {
          return this.formService.transformForSelect(
            timezones,
            (timezone) => timezone.id,
            (timezone) => timezone.name,
            null,
          );
        }),
      ),
    },
  ];

  ngOnInit(): void {
    super.ngOnInit();

    // * if FormGroup is passed from parent, populate it with values
    if (this.formGroup && this.outputSettings) {
      this.formService.populateFormValues(this.formGroup, this.outputSettings);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formGroup'] && this.formGroup && this.outputSettings) {
      this.formService.populateFormValues(this.formGroup, this.outputSettings);
    }
  }

  // * override parent's createFormGroup to prevent creating a new one
  createFormGroup(): void {
    if (this.formGroup) {
      this.formGroupChange.emit(this.formGroup);
    } else {
      // * fallback to parent's behavior if no FormGroup is passed
      super.createFormGroup();
    }
  }
}
